import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, sites } from '@/mocks/sites';
import type { Category } from '@/mocks/sites';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'download');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const filteredSites = sites.filter((s) => s.category === activeCategory);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - Galgame资源导航`,
    description: currentCategory.description,
    url: `https://galgame-resource.com/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredSites.map((site, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'WebSite',
          name: site.name,
          url: site.url,
          description: site.description,
        },
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory.name },
            ]}
          />

          {/* Category Header */}
          <section className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {currentCategory.description}
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Sites Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            {filteredSites.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无站点</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredSites.map((site) => (
                  <div
                    key={site.id}
                    className="p-5 rounded-lg bg-background-50 border border-background-200/70 transition-all duration-300"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={site.logoUrl}
                          alt={site.name}
                          title={site.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base text-foreground-900 mb-1">
                          {site.name}
                        </h3>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-xs ${
                                i < Math.floor(site.rating)
                                  ? 'ri-star-fill text-primary-500'
                                  : 'ri-star-line text-foreground-300'
                              }`}
                            ></i>
                          ))}
                          <span className="text-xs text-foreground-500 ml-1">
                            {site.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                      {site.description}
                    </p>
                    <div className="flex items-center flex-wrap gap-1.5 mb-4">
                      {site.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <i className="ri-external-link-line"></i>
                      访问站点
                    </a>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}